import React, { useState, useEffect, useCallback } from 'react';
import { FiLoader, FiCheckCircle, FiAlertCircle, FiRefreshCw, FiInfo } from 'react-icons/fi';
import Modal from '../../../components/Modal';
import { crosswalkApi, questionnairesApi } from '../../../services/api';

const STATUS_COLORS = {
  loading:      'text-slate-500 bg-slate-50',
  running:      'text-amber-600 bg-amber-50',
  processing:   'text-amber-600 bg-amber-50',
  polling:      'text-amber-600 bg-amber-50',
  enriching:    'text-purple-600 bg-purple-50',
  saving:       'text-indigo-600 bg-indigo-50',
  completed:    'text-green-600 bg-green-50',
  no_proposals: 'text-slate-600 bg-slate-50',
  failed:       'text-red-600 bg-red-50',
};

const ACTIVE_STATUSES = ['running', 'processing', 'polling', 'enriching', 'saving', 'queued'];
const LOADING_STATUSES = ['loading'];
const TERMINAL_STATUSES = ['completed', 'failed', 'no_proposals'];

const CrosswalkDetailsModal = ({ isOpen, onClose, assessment }) => {
  const [qnrDetails, setQnrDetails] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Fetch a single questionnaire row's current state.
  //  - On open (reload = false) we DO NOT call reloadSaved: that endpoint reconstructs the
  //    response from the persisted DB row and therefore echoes the DB's CrosswalkStatus, which
  //    can be stale (e.g. stuck on "polling" if the trigger-time job was orphaned by a restart).
  //    Instead we read proposals from the DB and let the upstream live-poll (getJobStatusBatch)
  //    drive the real status — so the badge reflects the true completed/failed/running state.
  //  - On Refresh (reload = true) we explicitly re-pull the saved result via reloadSaved.
  const loadRow = useCallback(async (qId, reload) => {
    try {
      const reloadRes = reload
        ? await crosswalkApi.reloadSaved(assessment.id, qId).catch(() => null)
        : null;
      const response = await crosswalkApi.getProposals(assessment.id, qId).catch(() => null);

      const proposals = response?.proposals || [];
      const jobId = reloadRes?.jobId || response?.jobId || null;
      // Proposals are only persisted on the completion path, so any proposal means the job
      // completed — even if the status field is stuck on an active value (orphaned job / expired
      // upstream cache). Presence of proposals therefore wins over a stale status. Otherwise seed
      // from the reported status (the upstream poll below corrects it to the live state).
      const apiStatus =
        proposals.length > 0 ? 'completed'
        : reloadRes?.status || response?.status || 'no_proposals';

      setQnrDetails(prev => prev.map(row =>
        row.questionnaireId === qId
          ? {
              ...row,
              proposalCount: proposals.length,
              status: apiStatus,
              jobId,
              phase: reloadRes?.phase ?? row.phase,
              // Only the explicit Refresh surfaces the reload message; a plain open stays quiet.
              message: reload ? (reloadRes?.message || reloadRes?.result?.message || null) : row.message,
              error: null,
            }
          : row
      ));
    } catch {
      setQnrDetails(prev => prev.map(row =>
        row.questionnaireId === qId
          ? { ...row, status: 'failed', error: 'Failed to fetch proposals' }
          : row
      ));
    }
  }, [assessment?.id]);

  // Initial load: list questionnaires immediately, then fetch each row's status individually
  // so the modal renders rows right away instead of waiting for all API calls to finish.
  useEffect(() => {
    if (!isOpen || !assessment) return;

    const init = async () => {
      setFetchLoading(true);
      setQnrDetails([]);

      try {
        const res = await questionnairesApi.getAll(1, 100, null, null, assessment.id);
        const items = res.items || res.data || (Array.isArray(res) ? res : []);

        // Render questionnaire rows immediately with a "loading" status.
        const initial = items.map((q) => ({
          questionnaireId: q.questionnaireId || q.id,
          questionnaireName: q.questionnaireName || q.name,
          proposalCount: 0,
          status: 'loading',
          jobId: null,
          phase: null,
          message: null,
          error: null,
        }));
        setQnrDetails(initial);
        setFetchLoading(false);

        // Fetch each questionnaire's real status independently (no reload on open).
        items.forEach((q) => loadRow(q.questionnaireId || q.id, false));
      } catch {
        setQnrDetails([]);
        setFetchLoading(false);
      }
    };

    init();
  }, [isOpen, assessment?.id, loadRow]); // eslint-disable-line react-hooks/exhaustive-deps

  // Manual Refresh: re-pull the saved/persisted result for every questionnaire on demand.
  const handleRefresh = useCallback(async () => {
    if (refreshing || qnrDetails.length === 0) return;
    setRefreshing(true);
    try {
      await Promise.all(qnrDetails.map(q => loadRow(q.questionnaireId, true)));
    } finally {
      setRefreshing(false);
    }
  }, [refreshing, qnrDetails, loadRow]);

  // Live status: fetch the upstream job status for EVERY job that has a job id — including
  // already-completed ones — so the badge and (dynamic) message reflect the real upstream
  // state instead of the value persisted in the DB at trigger time. Keep polling while any
  // job is still active, then stop.
  const allJobIdsKey = qnrDetails
    .filter(q => q.jobId)
    .map(q => q.jobId)
    .sort()
    .join(',');

  useEffect(() => {
    if (!isOpen || !allJobIdsKey) return;
    const jobIds = allJobIdsKey.split(',');

    let cancelled = false;
    let intervalId;

    const poll = async () => {
      try {
        const batch = await crosswalkApi.getJobStatusBatch(jobIds);
        if (cancelled) return;
        const byId = {};
        (batch || []).forEach(j => { if (j?.jobId) byId[j.jobId] = j; });

        setQnrDetails(prev => prev.map(q => {
          const live = q.jobId ? byId[q.jobId] : null;
          if (!live) return q;
          const liveCount = live.result?.proposals?.summary?.totalProposals
            ?? live.result?.persistence?.proposedCount;
          const proposalCount = (typeof liveCount === 'number' && liveCount > 0) ? liveCount : q.proposalCount;
          // Proposals present ⇒ completed, regardless of a stuck/expired upstream status.
          const status = proposalCount > 0 ? 'completed' : (live.status || q.status);
          return {
            ...q,
            status,
            phase: live.phase ?? q.phase,
            message: live.message || live.result?.message || q.message,
            proposalCount,
          };
        }));

        // Stop polling once nothing upstream is still in flight. A job that already has proposals
        // is done (completed), so it doesn't count as active even if its status field is stale.
        const anyActive = (batch || []).some(j => {
          if (!j || !ACTIVE_STATUSES.includes(j.status)) return false;
          const c = j.result?.proposals?.summary?.totalProposals ?? j.result?.persistence?.proposedCount ?? 0;
          return !(c > 0);
        });
        if (!anyActive && intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      } catch {
        /* transient error — retry on the next tick */
      }
    };

    poll();
    intervalId = setInterval(poll, 3000);
    return () => { cancelled = true; if (intervalId) clearInterval(intervalId); };
  }, [isOpen, allJobIdsKey]);

  const totalProposals   = qnrDetails.reduce((sum, q) => sum + q.proposalCount, 0);
  const completedCount   = qnrDetails.filter(q => q.status === 'completed').length;
  const noProposalCount  = qnrDetails.filter(q => q.status === 'no_proposals').length;
  const failedCount      = qnrDetails.filter(q => q.status === 'failed').length;
  const activeCount      = qnrDetails.filter(q => ACTIVE_STATUSES.includes(q.status)).length;
  const loadingCount     = qnrDetails.filter(q => LOADING_STATUSES.includes(q.status)).length;
  const finishedCount    = qnrDetails.filter(q => TERMINAL_STATUSES.includes(q.status)).length;

  const refreshDisabled = refreshing || fetchLoading || qnrDetails.length === 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Crosswalk Details — ${assessment?.accountName || `Assessment #${assessment?.id}`}`}
      size="lg"
      footer={
        <button
          type="button"
          onClick={handleRefresh}
          disabled={refreshDisabled}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#0b79b8] border border-[#0b79b8]/40 rounded-lg hover:bg-[#0b79b8]/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FiRefreshCw className={refreshing ? 'animate-spin' : ''} />
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      }
    >
      <div className="space-y-4">
        {/* Instruction banner */}
        <div className="flex items-start gap-3 bg-[#f0f7ff] border-l-4 border-[#0077b5]  px-4 py-3">
          <div>
            <p className="text-xs text-[#53545a] leading-relaxed">
              Monitor the progress of the crosswalk job as the MyRiskShield crosswalk engine generates proposed mappings. Once all proposals are available, review and validate the suggested crosswalks before accepting or refining them.
            </p>
          </div>
        </div>

        {/* Summary bar */}
        <div className="flex items-center gap-4 text-sm flex-wrap">
          {fetchLoading ? (
            <span className="inline-flex items-center gap-1.5 text-slate-500">
              <FiLoader className="animate-spin" /> Loading questionnaires…
            </span>
          ) : qnrDetails.length === 0 ? (
            <span className="text-slate-500">No questionnaires found for this assessment.</span>
          ) : loadingCount > 0 || activeCount > 0 ? (
            <span className="inline-flex items-center gap-1.5 text-amber-600">
              <FiLoader className="animate-spin" /> Fetching… {finishedCount}/{qnrDetails.length} done
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-green-600">
              <FiCheckCircle /> All jobs finished — {totalProposals} total proposal(s)
            </span>
          )}
          {completedCount  > 0 && <span className="text-green-600 font-medium">{completedCount} completed</span>}
          {noProposalCount > 0 && <span className="text-slate-500 font-medium">{noProposalCount} no proposals</span>}
          {failedCount     > 0 && <span className="text-red-600 font-medium">{failedCount} failed</span>}
        </div>

        {/* Questionnaire rows */}
        {qnrDetails.length > 0 && (
          <div className="divide-y divide-slate-100 border border-slate-200">
            {qnrDetails.map((q, i) => (
              <div key={q.questionnaireId || i} className="flex items-start justify-between px-4 py-3 gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800">
                    {q.questionnaireName || `Questionnaire #${q.questionnaireId}`}
                  </p>
                  {q.error ? (
                    <p className="text-xs text-red-500 mt-0.5">{q.error}</p>
                  ) : (
                    <p className={`text-xs mt-0.5 ${q.proposalCount > 0 ? 'text-green-600' : 'text-slate-500'}`}>
                      {q.proposalCount} proposal(s)
                    </p>
                  )}
                  {/* Live message from the job status, shown dynamically per current status */}
                  {q.message && (
                    <p className="text-xs text-slate-500 mt-0.5">{q.message}</p>
                  )}
                  {q.jobId && (
                    <p className="text-[11px] text-slate-400 font-mono mt-0.5 truncate">
                      Job ID: {q.jobId}
                    </p>
                  )}
                </div>

                <span className={`flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[q.status] || 'text-slate-600 bg-slate-50'}`}>
                  {q.status === 'loading'
                    ? <><FiLoader className="animate-spin text-xs" /> loading</>
                    : ACTIVE_STATUSES.includes(q.status)
                      ? <><FiLoader className="animate-spin text-xs" /> {q.status}</>
                      : q.status === 'completed'
                        ? <><FiCheckCircle className="text-xs" /> completed</>
                        : q.status === 'failed'
                          ? <><FiAlertCircle className="text-xs" /> failed</>
                          : q.status === 'no_proposals'
                            ? <><FiRefreshCw className="text-xs" /> no proposals</>
                            : <>{q.status}</>}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CrosswalkDetailsModal;
